import React, { ReactElement, useState } from 'react';
import styled from 'styled-components';
import { Color } from '../../../config/Color';
import { LayoutScreenConfig, ScreenType } from '../../../config/FlowTypes';
import { FlowContentBody, ScreenWrapper } from '../../styles/Containers';
import { BodySM, Heading3, HeroTitle } from '../../styles/Typography';
import { NavigationProps } from '../FlowNavigation';
import { LayoutActions } from '../layouts/LayoutActions';

// Empty layout: all crisis content is owned by the component (CRISIS_RESOURCES), not config.
export type MedicareAccessHelplinesLayoutConfig = Record<string, never>;

export type MedicareAccessHelplinesScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.MedicareAccessHelplines,
  MedicareAccessHelplinesLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  MedicareAccessHelplinesScreenConfig<ScreenNames>,
  ScreenNames
>;

type CrisisAction = {
  label: string;
  // tel:/sms: act when on mobile; https chat links open in a new tab on desktop. Every action is
  // shown on every platform — we never hide options based on the device.
  href: string;
};

type CrisisResource = {
  title: string;
  description: string;
  actions: CrisisAction[];
};

type CrisisActionType = 'call' | 'text' | 'chat';

const ACTION_PILL_COLOR: Record<CrisisActionType, string> = {
  call: '#87BEFF',
  text: '#ED8DB9',
  chat: '#6FB1A0',
};
const PILL_TEXT_COLOR = '#0A1412';

function crisisActionType(href: string): CrisisActionType {
  if (href.startsWith('tel:')) return 'call';
  if (href.startsWith('sms:')) return 'text';
  return 'chat';
}

const CRISIS_RESOURCES: CrisisResource[] = [
  {
    title: '988 Suicide and Crisis Lifeline',
    description: 'Call or text 988, or chat online.',
    actions: [
      { label: 'Call 988', href: 'tel:988' },
      { label: 'Text 988', href: 'sms:988' },
      { label: 'Chat online', href: 'https://chat.988lifeline.org/' },
    ],
  },
  {
    title: 'Veterans Crisis Line',
    description: 'Call 988 and press 1, text 838255, or chat online.',
    actions: [
      { label: 'Call 988, press 1', href: 'tel:988' },
      { label: 'Text 838255', href: 'sms:838255' },
      { label: 'Chat online', href: 'https://www.veteranscrisisline.net/get-help-now/chat/' },
    ],
  },
  {
    title: 'Crisis Text Line',
    description: 'Text HOME to 741741, or chat online.',
    actions: [
      { label: 'Text HOME to 741741', href: 'sms:741741?body=HOME' },
      { label: 'Chat online', href: 'https://connect.crisistextline.org/chat?source=text-us' },
    ],
  },
  {
    title: 'Emergency',
    description: 'Call 911 or go to your nearest emergency department.',
    actions: [{ label: 'Call 911', href: 'tel:911' }],
  },
];

export const MedicareAccessHelplinesScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const [openIndex, setOpenIndex] = useState<number>(0);

  function onContinue(): void {
    props.navigation.next(props);
  }

  return (
    <ScreenWrapper>
      <FlowContentBody>
        <Intro>
          <HeroTitle>Help is available right now</HeroTitle>
          <BodySM>
            What you’re describing is serious, and you deserve real support. Talking to someone
            trained to help can make a difference. They’re available any time, free, and
            confidential.
          </BodySM>
          <BodySM>
            If you’re in immediate danger, call 911 or go to your nearest emergency department.
          </BodySM>
          <Heading3>Ways to get support now:</Heading3>
        </Intro>
        {CRISIS_RESOURCES.map((resource, index) => {
          const isOpen = index === openIndex;
          return (
            <ResourceCard key={resource.title}>
              <ResourceHeader
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpenIndex(isOpen ? -1 : index)}
              >
                <Heading3>{resource.title}</Heading3>
                <Chevron isOpen={isOpen}>▾</Chevron>
              </ResourceHeader>
              {isOpen && (
                <ResourceBody>
                  <BodySM>{resource.description}</BodySM>
                  <ActionRow>
                    {resource.actions.map((action) => {
                      const type = crisisActionType(action.href);
                      return (
                        <ActionLink
                          key={action.label}
                          href={action.href}
                          actionType={type}
                          target={type === 'chat' ? '_blank' : undefined}
                          rel={type === 'chat' ? 'noreferrer' : undefined}
                        >
                          {action.label}
                        </ActionLink>
                      );
                    })}
                  </ActionRow>
                </ResourceBody>
              )}
            </ResourceCard>
          );
        })}
        <LayoutActions primaryButtonTitle="Continue" onPressPrimaryButton={onContinue} />
      </FlowContentBody>
    </ScreenWrapper>
  );
};

const Intro = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing._24px};
`;

const ResourceCard = styled.div`
  border: 1px solid ${Color.decorative.default};
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing._16px};
  margin: ${({ theme }) => `${theme.spacing._16px} 0`};
  max-width: 480px;
`;

const ResourceHeader = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing._8px};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
`;

const Chevron = styled.span<{ isOpen: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${({ isOpen }) => (isOpen ? '180deg' : '0deg')});
`;

const ResourceBody = styled.div`
  margin-top: ${({ theme }) => theme.spacing._8px};
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing._8px};
  margin-top: ${({ theme }) => theme.spacing._8px};
`;

const ActionLink = styled.a<{ actionType: CrisisActionType }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  background: ${({ actionType }) => ACTION_PILL_COLOR[actionType]};
  color: ${PILL_TEXT_COLOR};
`;
